import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { PanelsTopLeft, Redo2, Undo2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

interface Props {
  clips: Array<{ id: string; title: string }>;
  selectedClipId: string | null;
  onSelectClip: (id: string) => void;
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  /** The full editor for the same clip. */
  fullEditorPath: string;
}

// AI Studio's bar under the top bar: which clip, undo/redo of every edit (the
// assistant's included), and a way out to the full editor.
export function AiStudioBar({
  clips, selectedClipId, onSelectClip, canUndo, canRedo, onUndo, onRedo, fullEditorPath,
}: Props) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2 sm:px-4" data-testid="ai-studio-bar">
      <Select value={selectedClipId ?? undefined} onValueChange={onSelectClip}>
        <SelectTrigger className="h-9 w-[min(100%,320px)]" data-testid="ai-studio-clip-select" aria-label={t('aiAssistant.studio.clip')}>
          <SelectValue placeholder={t('aiAssistant.studio.clip')} />
        </SelectTrigger>
        <SelectContent>
          {clips.map((clip) => (
            <SelectItem key={clip.id} value={clip.id}>{clip.title}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <div className="flex items-center gap-1">
        <Button type="button" variant="ghost" size="icon" className="h-9 w-9" disabled={!canUndo} onClick={onUndo} aria-label={t('aiAssistant.studio.undo')} data-testid="ai-studio-undo">
          <Undo2 className="h-4 w-4" />
        </Button>
        <Button type="button" variant="ghost" size="icon" className="h-9 w-9" disabled={!canRedo} onClick={onRedo} aria-label={t('aiAssistant.studio.redo')} data-testid="ai-studio-redo">
          <Redo2 className="h-4 w-4" />
        </Button>
      </div>
      <Button asChild variant="outline" size="sm" className="ml-auto h-9">
        <Link to={fullEditorPath} data-testid="ai-studio-full-editor">
          <PanelsTopLeft className="h-4 w-4" />
          {t('aiAssistant.studio.fullEditor')}
        </Link>
      </Button>
    </div>
  );
}
